import { KeyboardEvent, useEffect, useMemo } from 'react';
import {
  ComparableListing,
  ComparableListings,
} from 'src/components/Listings/ComparableListings';
import { useActiveFocusContext } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getAllInPriceFromListPrice,
  getAllInPriceFromListPriceForListing,
} from 'src/utils/inventoryUtils';
import {
  ActivityLogClient,
  CompListing,
  Listing,
  ListingBroadcastStatus,
  ListingPriceCalculation,
  Marketplace,
} from 'src/WebApiController';

import * as styles from './ConnectedComparableListings.css';

export type ListingWithSeatScore = Listing & {
  seatScore?: number | null | undefined;
};

export type CompListingWithSeatScore = CompListing & {
  seatScore?: number | null | undefined;
};

export type ConnectedComparableListingsV2Props = {
  isLoading?: boolean;
  priceOverride?: number | null;
  compListings: CompListingWithSeatScore[];
  seatScore?: number | null;
  sortBy?: 'price' | 'dealScore';
  showAutoPriceResults?: boolean;
  isExcludingFanInventory?: boolean;
  isSortDescending?: boolean;
  listingPriceCalc?: ListingPriceCalculation | null;
  listings: Listing[]; // Seller listings
  selectedListingId?: number; // Your selected listing id
};

const mapListingToComparableListing = (
  listing: ListingWithSeatScore,
  isSelectedListing: boolean,
  listingPriceCalc?: ListingPriceCalculation | null
): ComparableListing => {
  // Should use price from pricer for the selected listing, and default to listing list price
  const listPrice = getAllInPriceFromListPriceForListing(listing);
  const websitePrice = isSelectedListing
    ? (listingPriceCalc?.websitePrice?.amt ?? listPrice)
    : listPrice;
  const autoPricingResultType =
    listingPriceCalc?.autoPricingResultType ?? undefined;
  const autoPricingResultConstraintType =
    listingPriceCalc?.autoPricingConstraintType ?? undefined;
  return {
    listingId: listing.id,
    section: listing.seating.section,
    row: listing.seating.row ?? undefined,
    seatFr: listing.seating.seatFr ?? undefined,
    seatTo: listing.seating.seatTo ?? undefined,
    price: websitePrice,
    unitCost: listing.unitCst?.amt ?? undefined,
    currencyCode: listing.currency,
    listingNotes: listing.seatTraits ?? undefined,
    // Price assuming same fees as seller’s account
    sellerNetProceeds: listing.listPrice,
    seatScore: listing.seatScore,
    isYourListing: isSelectedListing,
    isListed: listing.brdcstStatus === ListingBroadcastStatus.Broadcast,
    availableTickets: listing.availQty,
    validPurchaseQuantities: listing.vldPurQty,
    autoPricingResultType,
    autoPricingResultConstraintType,
    shouldShowResultTypeAction: listing.isAutoPrc ?? false,
  };
};

const mapCompListingToComparableListing = (
  compListing: CompListingWithSeatScore,
  listingPriceCalc?: ListingPriceCalculation | null
) => {
  const isAnchor = listingPriceCalc?.udrRefLstIds?.includes(
    compListing.listingId
  );

  return {
    listingId: compListing.listingId,
    section: compListing.section ?? undefined,
    row: compListing.row ?? undefined,
    seatFr: compListing.seatFrom ?? undefined,
    seatTo: compListing.seatTo ?? undefined,
    price: compListing.sellerAllInPrice?.amt,
    priceDisp: compListing.sellerAllInPrice?.disp,
    currencyCode: compListing.currencyCode ?? undefined,
    listingNotes: compListing.listingNotes ?? undefined,
    sellerNetProceeds: compListing.sellerNetProceeds?.amt ?? undefined,
    isOutlier:
      compListing.isWithoutOutlier == null
        ? false
        : compListing.isWithoutOutlier,
    isAnchor: isAnchor,
    isYourListing: false,
    isFanInventory: Boolean(compListing.isFanInventory),
    seatScore: compListing.seatScore,
    availableTickets: compListing.availableTickets,
    validPurchaseQuantities: compListing.validPurchaseQuantities,
  } as ComparableListing;
};

export const ConnectedComparableListingsV2 = ({
  isLoading,
  compListings,
  listings,
  selectedListingId = 0,
  listingPriceCalc,
  isExcludingFanInventory,
  ...rest
}: ConnectedComparableListingsV2Props) => {
  const { activeAccountWebClientConfig, loginContext } = useAppContext();

  const { onKeyDownHandler, activeItem } = useActiveFocusContext();
  const activeItemIndex = activeItem?.index;

  const onCurrentKeyDownHandler = (e: KeyboardEvent) => {
    onKeyDownHandler(e, activeItemIndex ?? 0);
  };

  const stubHubMkpSettings =
    loginContext?.user?.activeAccount?.marketplaceSettings?.find(
      (m) => m.mkp === Marketplace.StubHub
    );

  const currentListing = useMemo(() => {
    return listings.find((l) => selectedListingId === l.id);
  }, [listings, selectedListingId]);

  const comparableListingsLoaded = useMemo(() => {
    return !isLoading && currentListing?.id != null;
  }, [currentListing?.id, isLoading]);

  useEffect(() => {
    if (comparableListingsLoaded && currentListing) {
      new ActivityLogClient(activeAccountWebClientConfig).trackListingCompsView(
        currentListing.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparableListingsLoaded]);

  const hasBetterPrice = useMemo(() => {
    const newPrcInclFanInv = listingPriceCalc?.newPrcInclFanInv;
    const newPrcExclFanInv = listingPriceCalc?.websitePrice?.amt;

    if (!newPrcInclFanInv || !newPrcExclFanInv) {
      return false;
    }
    return newPrcInclFanInv < newPrcExclFanInv;
  }, [listingPriceCalc]);

  const currentListings = useMemo(() => {
    const filteredListings =
      listings.filter(
        (l) =>
          listingPriceCalc?.sellerAccountPosListingIds.includes(l.id) ||
          currentListing?.id === l.id
      ) ?? [];
    return filteredListings.map((l) =>
      mapListingToComparableListing(
        l,
        l.id === currentListing?.id,
        listingPriceCalc
      )
    );
  }, [currentListing?.id, listingPriceCalc, listings]);

  const comparableListings = useMemo(() => {
    return compListings.map((l) =>
      mapCompListingToComparableListing(l, listingPriceCalc)
    );
  }, [compListings, listingPriceCalc]);

  return (
    <Stack
      className={styles.root}
      direction="column"
      gap="l"
      onKeyDown={onCurrentKeyDownHandler}
    >
      {isExcludingFanInventory && hasBetterPrice && (
        <div className={styles.fanInventoryInfoContainer}>
          <InfoOutlineIcon
            fill={vars.color.textWarning}
            size={vars.iconSize.l}
          />
          <span className={styles.statementText}>
            <Content id={ContentId.NonCompetitiveListing} />
          </span>
        </div>
      )}
      {isLoading ? null : (
        <ComparableListings
          currentListings={currentListings}
          comparableListings={comparableListings}
          floorPrice={
            (listingPriceCalc?.cmpFlr?.amt ?? currentListing?.procsFloor)
              ? getAllInPriceFromListPrice(
                  listingPriceCalc?.cmpFlr?.amt ?? currentListing?.procsFloor,
                  currentListing,
                  stubHubMkpSettings?.sellerFee
                )
              : undefined
          }
          ceilingPrice={
            (listingPriceCalc?.cmpCeil?.amt ?? currentListing?.procsCeil)
              ? getAllInPriceFromListPrice(
                  listingPriceCalc?.cmpCeil?.amt ?? currentListing?.procsCeil,
                  currentListing,
                  stubHubMkpSettings?.sellerFee
                )
              : undefined
          }
          listingPriceCalc={listingPriceCalc}
          isExcludingFanInventory={isExcludingFanInventory}
          {...rest}
        />
      )}
    </Stack>
  );
};
